import type { ResultadoImgr } from "@caint/data";
import type { CicloPublico, FaixaImgr } from "@caint/shared";
import { useEffect, useMemo, useState } from "react";
import { mensagemAmigavel } from "../lib/erros.ts";
import { qrDataUri } from "../lib/qrcode.ts";

/** Cores das faixas do IMGR (Inicial→Avançado). */
const COR_FAIXA: Record<FaixaImgr, string> = {
  Inicial: "var(--danger)",
  Básico: "var(--risk-ra)",
  Intermediário: "var(--warning)",
  Aprimorado: "var(--mat-3)",
  Avançado: "var(--success)",
};

function pct(n: number | null): string {
  return n == null ? "—" : `${n.toFixed(1).replace(".", ",")}%`;
}

function BarraComponente({ nome, valor }: { nome: string; valor: number | null }) {
  const v = valor ?? 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
        <span>{nome}</span>
        <b>{pct(valor)}</b>
      </div>
      <div style={{ height: 7, borderRadius: 99, background: "var(--surface-2)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${v}%`, background: "var(--primary)", borderRadius: 99 }} />
      </div>
    </div>
  );
}

function ResultadoView({ r }: { r: ResultadoImgr }) {
  if (r.nRespondentes === 0) {
    return <p className="spec">Ainda sem respostas. Compartilhe o link/QR com a equipe.</p>;
  }
  const cor = r.faixa ? COR_FAIXA[r.faixa] : "var(--text-muted)";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            width: 72,
            height: 72,
            borderRadius: 16,
            background: cor,
            color: "#fff",
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1,
          }}
        >
          {pct(r.imgr)}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{r.faixa ?? "—"}</div>
          <div className="spec">
            IMGR · {r.nRespondentes} resposta{r.nRespondentes === 1 ? "" : "s"}
          </div>
          <div className="spec">
            {r.nGestores} gestor{r.nGestores === 1 ? "" : "es"} · {r.nLiderados} liderado
            {r.nLiderados === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", margin: "10px 0 8px" }}>
        Maturidade por componente
      </div>
      {r.porComponente.map((c) => (
        <BarraComponente key={c.id} nome={c.nome} valor={c.pct} />
      ))}
    </div>
  );
}

export interface FormularioPublicoPainelProps {
  ciclo: CicloPublico;
  carregarResultado: (cicloId: string) => Promise<ResultadoImgr>;
}

/** Painel reutilizável (mobile/desktop): QR + link público + resultado IMGR. */
export function FormularioPublicoPainel({ ciclo, carregarResultado }: FormularioPublicoPainelProps) {
  const url = useMemo(
    () => (ciclo.tokenPublico ? `${window.location.origin}/q/${ciclo.tokenPublico}` : null),
    [ciclo.tokenPublico],
  );
  const qr = useMemo(() => (url ? qrDataUri(url, { tamanho: 188 }) : null), [url]);
  const [copiado, setCopiado] = useState(false);
  const [res, setRes] = useState<ResultadoImgr | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setRes(null);
    setErro(null);
    carregarResultado(ciclo.id)
      .then((r) => vivo && setRes(r))
      .catch((e) => vivo && setErro(mensagemAmigavel(e)));
    return () => {
      vivo = false;
    };
  }, [ciclo.id, carregarResultado]);

  async function copiar() {
    if (!url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      setCopiado(false);
    }
  }

  if (!url || !qr) {
    return (
      <p className="spec">Este ciclo não possui formulário público (abra um novo ciclo para gerar o link).</p>
    );
  }

  return (
    <div>
      <div className="m-card pad" style={{ textAlign: "center" }}>
        <img
          src={qr}
          alt="QR Code do formulário público"
          width={188}
          height={188}
          style={{ display: "inline-block" }}
        />
        <div className="spec" style={{ wordBreak: "break-all", marginTop: 8 }}>
          {url}
        </div>
        <button
          type="button"
          className="m-btn primary"
          style={{ marginTop: 10 }}
          onClick={() => void copiar()}
        >
          {copiado ? "✓ Link copiado" : "Copiar link"}
        </button>
      </div>

      <div className="m-card pad" style={{ marginTop: 12 }}>
        <b style={{ display: "block", marginBottom: 10 }}>Resultado (IMGR)</b>
        {erro && <p style={{ color: "var(--danger)" }}>{erro}</p>}
        {!res && !erro && <p className="spec">Carregando resultado…</p>}
        {res && <ResultadoView r={res} />}
      </div>
    </div>
  );
}
